import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logEvent } from '../../analytics';
import { useTranslation } from '../../i18n';
import {
  IInspiration,
  IMainState,
  IProduct,
  MeetingRole,
  WishlistType
} from '../../interfaces';
import { actionDidViewPage, actionFocusItem } from '../../redux/actions';
import { actionFetchRelatedProductsForInspiration } from '../../redux/asyncActions';
import { DID_FOCUS_PRODUCT_IN_SLIDER } from '../../utils/constants';
import { getPrevNextUrlForInspirationsList } from '../../utils/inspiration';
import { PageView } from '../../utils/pageView';
import { getProductsFilterByBlackAndWhitelist } from '../../utils/product';
import { isEmbeddedInStreamingStudio } from '../../utils/streamingstudio';
import {
  getParentWindowMeetingRole,
  isParentWindowInMeeting
} from '../../utils/window';
import { AppContext } from '../AppContext';
import { ChatContentSharingContext } from '../ChatContentSharingContext';
import EyeballTimeLogger from '../EyeballTimeLogger';
import Header from '../Header';
import NextPrevNav from '../Navigation/NextPrevNav';
import PageViewLogger from '../PageViewLogger';
import ProductsSlider from '../Product/ProductSlider';
import WishlistButton from '../Wishlist/WishlistButton';
import { renderInspiration } from './common';

interface IInspirationWithRelatedProductsPageProps {
  inspiration: IInspiration;
  pageViewType?: PageView;
}

const InspirationWithRelatedProductsPage = ({
  inspiration,
  pageViewType = PageView.INSPIRATION
}: IInspirationWithRelatedProductsPageProps) => {
  const [isAdvisorInMeeting, setIsAdvisorInMeeting] = React.useState(false);

  const inspirations = useSelector(
    (state: IMainState) => state.clientState.inspirationsList
  );
  const inspirationRelatedProducts = useSelector(
    (state: IMainState) => state.clientState.inspirationRelatedProducts || {}
  );

  const focusedItem = useSelector(
    (state: IMainState) => state.clientState.focusedItem
  );

  const handleFocusItem = (itemIndex: string) => {
    dispatch(actionFocusItem({ section: 'productsSlider', itemId: itemIndex }));
    logEvent(DID_FOCUS_PRODUCT_IN_SLIDER, DID_FOCUS_PRODUCT_IN_SLIDER, {
      itemIndex
    });
  };

  const isLoadingProducts = inspirationRelatedProducts.loading;

  const { storeConfig } = React.useContext(AppContext);
  const { isChatContentSelection, getSendToShareButton } = React.useContext(
    ChatContentSharingContext
  );
  const productsFilter = getProductsFilterByBlackAndWhitelist(
    storeConfig?.productBlacklist,
    storeConfig?.productWhitelist
  );
  const productsOfSameBrand: IProduct[] = (
    (inspirationRelatedProducts.data || {})[inspiration.id] || []
  ).filter((p) => {
    const currentBrandId = storeConfig?.brandId;
    if (currentBrandId) {
      return p.brandId === currentBrandId;
    }
    return true;
  });
  const products = productsFilter(productsOfSameBrand);

  const dispatch = useDispatch();

  const { t } = useTranslation();

  React.useEffect(() => {
    dispatch(actionDidViewPage(pageViewType));
    dispatch(actionFetchRelatedProductsForInspiration(inspiration));
  }, [inspiration.id]);

  React.useEffect(() => {
    setIsAdvisorInMeeting(
      (isParentWindowInMeeting() &&
        getParentWindowMeetingRole() === MeetingRole.ADVISOR) ||
        isEmbeddedInStreamingStudio()
    );
  }, []);

  const [prevUrl, nextUrl] = getPrevNextUrlForInspirationsList(
    inspirations,
    inspiration
  );

  const { id, title, senderImgUrl, type, context } = inspiration;

  const inspirationTitleHeader = React.useMemo(() => {
    if (type === 'scene') {
      return title;
    }
    return (context && context.toUpperCase()) || t('inspiration').toUpperCase();
  }, [inspiration]);

  return (
    <PageViewLogger
      key={inspiration.id}
      pageView={PageView.INSPIRATION}
      brandId={storeConfig?.brandId || inspiration.sender || ''}
      uuid={inspiration.id}
    >
      <EyeballTimeLogger
        key={inspiration.id}
        tracker={{ view: PageView.INSPIRATION, id: inspiration.id }}
      >
        <div
          className={`inspiration-page inspiration-${
            inspiration.id
          } ${context?.toUpperCase()} ${inspirations && 'hasnav'}`}
        >
          <Header
            title={inspirationTitleHeader}
            shareTitle={title}
            shareImage={senderImgUrl}
            type={pageViewType}
            id={id}
          />
          {inspirations && inspirations.length && (
            <NextPrevNav
              nextLabel={t('next')}
              prevLabel={t('prev')}
              next={nextUrl}
              prev={prevUrl}
            />
          )}
          {type && renderInspiration(inspiration)}
          <WishlistButton
            item={{
              itemId: inspiration.id,
              itemType: WishlistType.INSPIRATION
            }}
            addLabel={t('add_to_favorites')}
            successLabel={t('in_my_favorites')}
          />
        </div>
        <div id="product-slider-anchor" />
        <ProductsSlider
          sectionTitle={t('featured_products').toUpperCase()}
          products={products}
          loading={isLoadingProducts}
          key={inspiration.id}
          focusedItem={
            focusedItem?.section === 'productsSlider'
              ? focusedItem?.itemId
              : undefined
          }
          onFocusItem={isAdvisorInMeeting ? handleFocusItem : undefined}
        />
        {isChatContentSelection &&
          getSendToShareButton(PageView.INSPIRATION, inspiration.id)}
        <style jsx>{`
          .inspiration-page {
            position: relative;
            padding-top: ${inspirations?.length ? '44px' : '0'};
          }

          .inspiration-page :global(.wishlist-button) {
            position: fixed;
            right: 0;
            bottom: 30px;
            background: rgba(255, 255, 255, 0.8);
            padding: 5px 10px;
            border-radius: 5px 0 0 5px;
            border: 1px solid #ccc;
            border-width: 1px 0 1px 1px;
            font-size: 0.8em;
            z-index: 2;
          }
          .inspiration-page :global(.btn.prev) {
            padding-left: 5px;
          }

          .inspiration-page :global(.btn.next) {
            padding-right: 5px;
          }

          .inspiration-page :global(.media-container) {
            background-color: #fff;
            padding: 0;
            text-align: center;
            position: relative;
            margin: auto;
          }
          .inspiration-page :global(.media-container.video-container) {
            background-color: #000;
          }
          .inspiration-page :global(.media-container > img) {
            width: 100%;
            display: block;
            margin: auto;
          }

          .inspiration-page :global(nav) {
            left: 0;
            right: 0;
            position: fixed;
            background: rgba(255, 255, 255, 0.5);
            z-index: 10;
            top: 46px;
          }

          @media (min-width: 768px) {
            .inspiration-page :global(.media-container > img) {
              width: auto;
              max-width: 100%;
            }

            .inspiration-page :global(nav) {
              top: 50px;
            }

            .inspiration-page :global(nav) {
              padding: 0 10px;
            }
          }
          .inspiration-page :global(.media-container .avatar) {
            width: 40px;
            height: 40px;
            position: absolute;
            left: 10px;
            top: 10px;
          }

          .inspiration-page :global(.content .copy) {
            padding: 40px 20px;
            text-align: center;
            overflow-wrap: break-word;
          }
          @media (min-width: 768px) {
            .inspiration-page :global(.content .copy) {
              text-align: left;
              padding: 0 40px;
            }

            .inspiration-page :global(.wishlist-button) {
              bottom: 120px;
            }
          }
        `}</style>
      </EyeballTimeLogger>
    </PageViewLogger>
  );
};

export default InspirationWithRelatedProductsPage;
